import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  api,
  areaDeDibujo,
  type CatalogoEtiquetas,
  type ElementoEtiqueta,
  type EtiquetaPlantilla,
  type Orientacion,
  type PlantillaEtiqueta,
  type UsoEtiqueta,
} from '../../api';
import { mensajeDeError } from '../../lib/useApi';
import { Icono, Numero } from '../../components/ui';
import Etiqueta, { ElementoDibujado, resolver } from '../../components/Etiqueta';
import { anchoNecesarioMm, code128, trazar } from '../../lib/code128';
import { imprimirEtiquetas } from '../../lib/imprimir';
import { acomodarDentro, alSistemaDelElemento, huellaGirada, normalizarGiro, type Caja } from '../../lib/giro';
import {
  ajustarConGuias,
  alinear,
  cajaDe,
  conSusGrupos,
  distribuir,
  siguienteGrupo,
  type Alineacion,
  type Guia,
} from '../../lib/lienzo';
import { aviso, botonPrimario, botonSecundario, c, campo, mono, nota, rotulo, tarjeta } from '../../theme';
import { ICONOS, NOMBRES, PASO_MM, aCuadricula, acotar, ajustar, duplicarElemento, elementoNuevo } from './comun';
import { Papel, Propiedades, Variables } from './paneles';
import { abrir, desplegar, resorte } from '../../components/movimiento';

/**
 * El lienzo del diseñador de etiquetas.
 *
 * La idea es que se sienta como cualquier editor de diseño: reglas al costado,
 * varios elementos elegidos a la vez, alinear y repartir, agrupar, y guías que
 * aparecen solas cuando algo se está por alinear con otra cosa. Lo que decide
 * dónde queda cada cosa vive en `lib/lienzo.ts` y `lib/giro.ts`, que se pueden
 * probar sin montar nada; acá queda el dibujo y los gestos.
 */

/** Cuadrículas que se ofrecen, en milímetros. 0 es sin cuadrícula. */
const CUADRICULAS = [0, 1, 2, 5];

const REGLA = 18; // grosor de las reglas, en píxeles

type Arrastre = {
  modo: 'mover' | 'medir';
  ids: string[];
  desde: Map<string, { x: number; y: number }>;
  inicial: ElementoEtiqueta;
  cajaInicial: Caja;
  xM: number;
  yM: number;
  movio: boolean;
  /** Si se suelta sin mover, la selección se queda con este solo. */
  solo: string | null;
};

type Marco = { x0: number; y0: number; x1: number; y1: number };

/** Reglas en milímetros, del mismo largo que el lienzo. */
function Reglas({
  area,
  zoom,
  seleccion,
}: {
  area: { ancho: number; alto: number };
  zoom: number;
  seleccion: Caja | null;
}) {
  const marcas = (largo: number) => {
    const paso = largo > 120 ? 10 : 5;
    return Array.from({ length: Math.floor(largo / paso) + 1 }, (_, i) => i * paso);
  };

  const comun = { background: c.panel, color: c.texto4, fontSize: 7.5, fontFamily: mono } as const;

  return (
    <>
      {/* Arriba */}
      <div style={{ gridArea: 'arriba', position: 'relative', ...comun, borderBottom: `1px solid ${c.borde}` }}>
        {seleccion && (
          <span
            style={{
              position: 'absolute',
              left: seleccion.x * zoom,
              width: Math.max(seleccion.ancho * zoom, 1),
              top: 0,
              bottom: 0,
              background: c.verdeSuave,
            }}
          />
        )}
        {marcas(area.ancho).map((mm) => (
          <span key={mm} style={{ position: 'absolute', left: mm * zoom, top: 0, bottom: 0 }}>
            <span style={{ position: 'absolute', left: 0, top: 0, height: 5, borderLeft: `1px solid ${c.bordeFuerte}` }} />
            <span style={{ position: 'absolute', left: 2, top: 5 }}>{mm}</span>
          </span>
        ))}
      </div>

      {/* Izquierda */}
      <div style={{ gridArea: 'izquierda', position: 'relative', ...comun, borderRight: `1px solid ${c.borde}` }}>
        {seleccion && (
          <span
            style={{
              position: 'absolute',
              top: seleccion.y * zoom,
              height: Math.max(seleccion.alto * zoom, 1),
              left: 0,
              right: 0,
              background: c.verdeSuave,
            }}
          />
        )}
        {marcas(area.alto).map((mm) => (
          <span key={mm} style={{ position: 'absolute', top: mm * zoom, left: 0, right: 0 }}>
            <span style={{ position: 'absolute', top: 0, left: 0, width: 5, borderTop: `1px solid ${c.bordeFuerte}` }} />
            <span style={{ position: 'absolute', top: 1, left: 6, lineHeight: 1 }}>{mm}</span>
          </span>
        ))}
      </div>

      <div style={{ gridArea: 'esquina', background: c.panel, borderRight: `1px solid ${c.borde}`, borderBottom: `1px solid ${c.borde}` }} />
    </>
  );
}

/** Un botón chico de la barra de herramientas. */
function Herramienta({
  titulo,
  icono,
  activo,
  deshabilitado,
  onClic,
}: {
  titulo: string;
  icono: string;
  activo?: boolean;
  deshabilitado?: boolean;
  onClic: () => void;
}) {
  return (
    <motion.button
      type="button"
      onClick={onClic}
      disabled={deshabilitado}
      title={titulo}
      aria-label={titulo}
      aria-pressed={activo}
      initial={false}
      animate={{
        backgroundColor: activo ? c.verdeSuave : c.panel,
        borderColor: activo ? c.verde : c.bordeFuerte,
        opacity: deshabilitado ? 0.4 : 1,
      }}
      transition={{ duration: 0.2 }}
      style={{
        width: 34,
        height: 34,
        display: 'grid',
        placeItems: 'center',
        borderRadius: 9,
        cursor: deshabilitado ? 'default' : 'pointer',
        border: '1px solid',
      }}
    >
      <Icono nombre={icono} size={17} color={activo ? c.verde : c.texto3} />
    </motion.button>
  );
}

export default function Disenador({
  etiqueta,
  catalogo,
  onVolver,
}: {
  etiqueta: EtiquetaPlantilla;
  catalogo: CatalogoEtiquetas;
  onVolver: () => void;
}) {
  const [nombre, setNombre] = useState(etiqueta.nombre);
  const [medida, setMedida] = useState({ ancho: etiqueta.ancho, alto: etiqueta.alto });
  const [orientacion, setOrientacion] = useState<Orientacion>(etiqueta.orientacion);
  const [uso, setUso] = useState<UsoEtiqueta>(etiqueta.uso);
  const [historial, setHistorial] = useState<PlantillaEtiqueta[]>([etiqueta.plantilla]);
  const [paso, setPaso] = useState(0);
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const [zoom, setZoom] = useState(2.4);
  const [cuadricula, setCuadricula] = useState(1);
  const [conGuias, setConGuias] = useState(true);
  const [guias, setGuias] = useState<Guia[]>([]);
  const [marco, setMarco] = useState<Marco | null>(null);
  const [conDatos, setConDatos] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [avisoTexto, setAvisoTexto] = useState<{ texto: string; ok: boolean } | null>(null);

  const svg = useRef<SVGSVGElement>(null);
  const arrastre = useRef<Arrastre | null>(null);
  const marcando = useRef<{ x: number; y: number } | null>(null);
  const portapapeles = useRef<ElementoEtiqueta[]>([]);

  const plantilla = historial[paso]!;
  const area = areaDeDibujo(medida.ancho, medida.alto, orientacion);
  const datos = conDatos ? catalogo.muestra : {};

  const elegidos = useMemo(
    () => plantilla.elementos.filter((e) => seleccion.has(e.id)),
    [plantilla.elementos, seleccion],
  );
  const uno = elegidos.length === 1 ? elegidos[0]! : null;
  const cajaSeleccion = useMemo(() => cajaDe(elegidos), [elegidos]);
  const etiquetaCaja: Caja = { x: 0, y: 0, ancho: area.ancho, alto: area.alto };

  // ------------------------------------------------------------- historial

  function cambiar(siguiente: PlantillaEtiqueta, enCurso = false) {
    if (enCurso) {
      setHistorial((h) => h.map((p, i) => (i === paso ? siguiente : p)));
      return;
    }
    const hasta = historial.slice(0, paso + 1);
    setHistorial([...hasta.slice(-40), siguiente]);
    setPaso(Math.min(hasta.length, 40));
  }

  const conElementos = (elementos: ElementoEtiqueta[], enCurso = false) =>
    cambiar({ ...plantilla, elementos }, enCurso);

  const cambiarElemento = (id: string, campos: Partial<ElementoEtiqueta>, enCurso = false) =>
    conElementos(
      plantilla.elementos.map((e) => (e.id === id ? { ...e, ...campos } : e)),
      enCurso,
    );

  /** Aplica posiciones nuevas a varios de una. */
  const mover = (posiciones: Record<string, { x: number; y: number }>, enCurso = false) =>
    conElementos(
      plantilla.elementos.map((e) => (posiciones[e.id] ? { ...e, ...posiciones[e.id]! } : e)),
      enCurso,
    );

  // -------------------------------------------------------------- acciones

  function agregar(tipo: ElementoEtiqueta['tipo']) {
    const nuevo = elementoNuevo(tipo, area.ancho, area.alto);
    conElementos([...plantilla.elementos, nuevo]);
    setSeleccion(new Set([nuevo.id]));
  }

  function borrar(ids: Iterable<string>) {
    const fuera = new Set(ids);
    conElementos(plantilla.elementos.filter((e) => !fuera.has(e.id)));
    setSeleccion(new Set());
  }

  function duplicar() {
    if (elegidos.length === 0) return;
    const copias = elegidos.map((e) => duplicarElemento(e));
    conElementos([...plantilla.elementos, ...copias]);
    setSeleccion(new Set(copias.map((e) => e.id)));
  }

  /** El orden del arreglo es el orden de dibujo: el último queda encima. */
  function capa(hacia: 'arriba' | 'abajo') {
    if (!uno) return;
    const i = plantilla.elementos.findIndex((e) => e.id === uno.id);
    const j = hacia === 'arriba' ? i + 1 : i - 1;
    if (i < 0 || j < 0 || j >= plantilla.elementos.length) return;
    const orden = [...plantilla.elementos];
    [orden[i], orden[j]] = [orden[j]!, orden[i]!];
    conElementos(orden);
  }

  /** Con uno elegido se alinea contra la etiqueta; con varios, entre ellos. */
  const alinearElegidos = (como: Alineacion) =>
    mover(alinear(elegidos, como, elegidos.length > 1 ? cajaSeleccion ?? etiquetaCaja : etiquetaCaja));

  const repartir = (eje: 'horizontal' | 'vertical') => mover(distribuir(elegidos, eje));

  const hayGrupo = elegidos.some((e) => e.grupo);

  function agrupar() {
    if (elegidos.length < 2) return;
    const grupo = siguienteGrupo(plantilla.elementos);
    const dentro = new Set(elegidos.map((e) => e.id));
    conElementos(plantilla.elementos.map((e) => (dentro.has(e.id) ? { ...e, grupo } : e)));
  }

  function desagrupar() {
    const dentro = new Set(elegidos.map((e) => e.id));
    conElementos(plantilla.elementos.map((e) => (dentro.has(e.id) ? { ...e, grupo: null } : e)));
  }

  // --------------------------------------------------------------- gestos

  function aMm(e: { clientX: number; clientY: number }) {
    const el = svg.current;
    if (!el) return null;
    const p = el.createSVGPoint();
    p.x = e.clientX;
    p.y = e.clientY;
    const m = el.getScreenCTM();
    if (!m) return null;
    const q = p.matrixTransform(m.inverse());
    return { x: q.x, y: q.y };
  }

  function alBajarElemento(ev: React.PointerEvent, el: ElementoEtiqueta, modo: 'mover' | 'medir') {
    ev.stopPropagation();

    // Con Ctrl o Shift se suma a lo elegido. Sin nada: si el elemento ya
    // estaba elegido no se toca la selección —así se arrastra el bloque
    // entero— y si no, se empieza de nuevo con ése. Tocar uno de un grupo
    // trae a todo el grupo.
    const suma = ev.ctrlKey || ev.metaKey || ev.shiftKey;
    let base: Set<string>;
    if (suma) {
      base = new Set(seleccion);
      if (base.has(el.id)) base.delete(el.id);
      else base.add(el.id);
    } else {
      base = seleccion.has(el.id) ? new Set(seleccion) : new Set([el.id]);
    }
    const completa = conSusGrupos(plantilla.elementos, base);
    setSeleccion(completa);

    const p = aMm(ev);
    if (!p) return;
    (ev.target as Element).setPointerCapture?.(ev.pointerId);

    const ids = modo === 'medir' ? [el.id] : [...completa];
    const desde = new Map(
      plantilla.elementos.filter((e) => ids.includes(e.id)).map((e) => [e.id, { x: e.x, y: e.y }]),
    );
    const cajaInicial = cajaDe(plantilla.elementos.filter((e) => ids.includes(e.id))) ?? huellaGirada(el);

    cambiar({ ...plantilla, elementos: [...plantilla.elementos] });
    arrastre.current = { modo, ids, desde, inicial: el, cajaInicial, xM: p.x, yM: p.y, movio: false, solo: suma ? null : el.id };
  }

  function alBajarFondo(ev: React.PointerEvent) {
    const p = aMm(ev);
    if (!p) return;
    if (!(ev.ctrlKey || ev.metaKey || ev.shiftKey)) setSeleccion(new Set());
    marcando.current = p;
    setMarco({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
  }

  function alMover(ev: React.PointerEvent) {
    const p = aMm(ev);
    if (!p) return;

    if (marcando.current) {
      setMarco({ x0: marcando.current.x, y0: marcando.current.y, x1: p.x, y1: p.y });
      return;
    }

    const a = arrastre.current;
    if (!a) return;
    a.movio = true;

    const dx = p.x - a.xM;
    const dy = p.y - a.yM;

    if (a.modo === 'medir') {
      // El mouse se mueve en la etiqueta, pero el ancho y el alto son del
      // elemento: girado, hay que llevar el arrastre a su sistema.
      const propio = alSistemaDelElemento(dx, dy, a.inicial.giro);
      cambiarElemento(
        a.inicial.id,
        {
          ancho: acotar(ajustar(a.inicial.ancho + propio.dx), PASO_MM, area.ancho * 2),
          alto: acotar(ajustar(a.inicial.alto + propio.dy), PASO_MM, area.alto * 2),
        },
        true,
      );
      return;
    }

    // Se mueve la caja de todo lo elegido y después se reparte el corrimiento:
    // así lo elegido se mantiene junto y se engancha como un solo bloque.
    const propuesta: Caja = {
      ...a.cajaInicial,
      x: aCuadricula(a.cajaInicial.x + dx, cuadricula),
      y: aCuadricula(a.cajaInicial.y + dy, cuadricula),
    };

    let encontradas: Guia[] = [];
    if (conGuias) {
      const otros = plantilla.elementos.filter((e) => !a.ids.includes(e.id)).map(huellaGirada);
      const r = ajustarConGuias(propuesta, otros, etiquetaCaja, 4 / zoom);
      propuesta.x += r.dx;
      propuesta.y += r.dy;
      encontradas = r.guias;
    }
    setGuias(encontradas);

    // Que el bloque no se vaya de la etiqueta.
    const corrX = acotar(propuesta.x, 0, Math.max(area.ancho - propuesta.ancho, 0)) - a.cajaInicial.x;
    const corrY = acotar(propuesta.y, 0, Math.max(area.alto - propuesta.alto, 0)) - a.cajaInicial.y;

    const posiciones: Record<string, { x: number; y: number }> = {};
    for (const [id, desde] of a.desde) posiciones[id] = { x: desde.x + corrX, y: desde.y + corrY };
    mover(posiciones, true);
  }

  function alSoltar() {
    if (marcando.current && marco) {
      const caja: Caja = {
        x: Math.min(marco.x0, marco.x1),
        y: Math.min(marco.y0, marco.y1),
        ancho: Math.abs(marco.x1 - marco.x0),
        alto: Math.abs(marco.y1 - marco.y0),
      };
      // Un marco de la nada es un clic en el vacío, no una selección.
      if (caja.ancho > 0.5 || caja.alto > 0.5) {
        const tocados = plantilla.elementos.filter((e) => {
          const h = huellaGirada(e);
          return (
            h.x < caja.x + caja.ancho && caja.x < h.x + h.ancho && h.y < caja.y + caja.alto && caja.y < h.y + h.alto
          );
        });
        setSeleccion(conSusGrupos(plantilla.elementos, new Set(tocados.map((e) => e.id))));
      }
    }

    const a = arrastre.current;
    if (a && !a.movio && a.solo && a.modo === 'mover') {
      setSeleccion(conSusGrupos(plantilla.elementos, new Set([a.solo])));
    }

    marcando.current = null;
    setMarco(null);
    setGuias([]);
    setTimeout(() => {
      arrastre.current = null;
    }, 0);
  }

  // -------------------------------------------------------------- guardar

  async function guardar() {
    setGuardando(true);
    setAvisoTexto(null);
    try {
      await api.guardarEtiqueta(etiqueta.id, {
        nombre: nombre.trim() || etiqueta.nombre,
        ancho: medida.ancho,
        alto: medida.alto,
        uso,
        orientacion,
        plantilla,
      });
      setAvisoTexto({ texto: 'Etiqueta guardada.', ok: true });
    } catch (err) {
      setAvisoTexto({ texto: mensajeDeError(err), ok: false });
    } finally {
      setGuardando(false);
    }
  }

  /**
   * Abre el código solo y bien grande para poder pasarle el lector.
   *
   * En el lienzo no se puede: al 100%, la barra más fina de un código mide un
   * píxel y ninguna cámara resuelve eso. Impreso no pasa —son tres o cuatro
   * puntos de impresora— pero para probar antes de gastar rollo hay que verlo
   * grande.
   */
  function probarCodigo() {
    if (!uno) return;
    const dibujo = document.getElementById(`wms-codigo-${uno.id}`)?.innerHTML;
    if (!dibujo) return;

    const ventana = window.open('', '_blank', 'width=760,height=460');
    if (!ventana) {
      setAvisoTexto({ texto: 'El navegador bloqueó la ventana. Permitila y probá de nuevo.', ok: false });
      return;
    }
    ventana.document.write(
      `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Probar el código</title>` +
        `<style>body{margin:0;display:grid;place-items:center;min-height:100vh;background:#fff;` +
        `font-family:system-ui,sans-serif}svg{width:92vw;max-width:900px;height:auto}` +
        `p{color:#6b7280;font-size:13px;margin:16px}</style></head><body><div>${dibujo}` +
        `<p>Pasale el lector o la cámara. Si acá lee, impreso también.</p></div></body></html>`,
    );
    ventana.document.close();
    ventana.focus();
  }

  function imprimirPrueba() {
    const dibujo = document.getElementById('wms-etiqueta-impresion')?.innerHTML;
    if (!dibujo) return;

    // Por la misma puerta que las etiquetas de verdad: la medida del papel y
    // el tamaño del dibujo son las mismas reglas, y tenerlas en dos lados es
    // tener una prueba que no se parece a lo que después sale del rollo.
    const problema = imprimirEtiquetas({ nombre, ancho: medida.ancho, alto: medida.alto }, [dibujo]);
    if (problema) setAvisoTexto({ texto: problema, ok: false });
  }

  // -------------------------------------------------------------- teclado

  useEffect(() => {
    const alTeclear = (ev: KeyboardEvent) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes((ev.target as HTMLElement)?.tagName)) return;
      const control = ev.ctrlKey || ev.metaKey;
      const tecla = ev.key.toLowerCase();

      if ((ev.key === 'Delete' || ev.key === 'Backspace') && seleccion.size) {
        ev.preventDefault();
        borrar(seleccion);
        return;
      }
      if (ev.key === 'Escape') {
        setSeleccion(new Set());
        return;
      }

      if (control && tecla === 'z') {
        ev.preventDefault();
        if (ev.shiftKey) setPaso((p) => Math.min(p + 1, historial.length - 1));
        else setPaso((p) => Math.max(p - 1, 0));
        return;
      }
      if (control && tecla === 'y') {
        ev.preventDefault();
        setPaso((p) => Math.min(p + 1, historial.length - 1));
        return;
      }
      if (control && tecla === 'a') {
        ev.preventDefault();
        setSeleccion(new Set(plantilla.elementos.map((e) => e.id)));
        return;
      }
      if (control && tecla === 'c') {
        portapapeles.current = elegidos;
        return;
      }
      if (control && tecla === 'v') {
        ev.preventDefault();
        if (portapapeles.current.length === 0) return;
        const copias = portapapeles.current.map((e) => duplicarElemento(e));
        conElementos([...plantilla.elementos, ...copias]);
        setSeleccion(new Set(copias.map((e) => e.id)));
        return;
      }
      if (control && tecla === 'd') {
        ev.preventDefault();
        duplicar();
        return;
      }
      if (control && tecla === 's') {
        ev.preventDefault();
        void guardar();
        return;
      }
      if (control && tecla === 'g') {
        ev.preventDefault();
        if (ev.shiftKey) desagrupar();
        else agrupar();
        return;
      }

      // Las flechas mueven al paso de la cuadrícula; con Shift, de a diez.
      const flechas: Record<string, [number, number]> = {
        ArrowLeft: [-1, 0],
        ArrowRight: [1, 0],
        ArrowUp: [0, -1],
        ArrowDown: [0, 1],
      };
      const flecha = flechas[ev.key];
      if (flecha && seleccion.size) {
        ev.preventDefault();
        const salto = (cuadricula || PASO_MM) * (ev.shiftKey ? 10 : 1);
        const posiciones: Record<string, { x: number; y: number }> = {};
        for (const e of elegidos) {
          const suelto = { ...e, x: e.x + flecha[0] * salto, y: e.y + flecha[1] * salto };
          posiciones[e.id] = acomodarDentro(suelto, area.ancho, area.alto);
        }
        mover(posiciones);
      }
    };

    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
    // Sin lista de dependencias a propósito: los atajos tienen que ver siempre
    // lo elegido y la plantilla del momento, no los del primer dibujo.
  });

  // --------------------------------------------------------------- dibujo

  const lineasCuadricula = useMemo(() => {
    if (!cuadricula) return [];
    // Se dibuja cada 5 mm aunque la cuadrícula sea de 1: una línea por
    // milímetro tapa el diseño.
    const paso = Math.max(cuadricula, 5);
    const lineas: { x1: number; y1: number; x2: number; y2: number }[] = [];
    for (let x = paso; x < area.ancho; x += paso) lineas.push({ x1: x, y1: 0, x2: x, y2: area.alto });
    for (let y = paso; y < area.alto; y += paso) lineas.push({ x1: 0, y1: y, x2: area.ancho, y2: y });
    return lineas;
  }, [cuadricula, area.ancho, area.alto]);

  const fuera = plantilla.elementos.filter((e) => {
    const h = huellaGirada(e);
    return h.x < -0.05 || h.y < -0.05 || h.x + h.ancho > area.ancho + 0.05 || h.y + h.alto > area.alto + 0.05;
  }).length;

  const varios = elegidos.length > 1;

  // Con qué ancho va a salir la barra más fina del código elegido: es lo que
  // decide si el lector lo va a poder leer, y no se ve mirando el dibujo. Se
  // mide sobre el trazado de verdad —módulo redondeado a puntos de impresora y
  // zonas mudas adentro— y no sobre el ancho del elemento dividido módulos, que
  // daba un número más lindo que el que después sale impreso.
  const barra = (() => {
    if (uno?.tipo !== 'barras') return null;
    const codigo = code128(resolver(uno.contenido ?? '', catalogo.muestra));
    if (!codigo) return null;
    const t = trazar(codigo, uno.ancho);
    return { modulo: t.modulo, puntos: t.puntos, apretado: t.apretado, necesita: anchoNecesarioMm(codigo) };
  })();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* ------------------------------------------------------------ barra */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button type="button" onClick={onVolver} style={{ ...botonSecundario, minHeight: 38 }}>
          <Icono nombre="arrow_back" size={17} color={c.texto3} />
          Volver
        </button>

        <input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          aria-label="Nombre de la etiqueta"
          style={{ ...campo, width: 200, height: 38 }}
        />

        <span style={{ fontSize: 12.5, color: c.texto3, fontFamily: mono }}>
          {medida.ancho} × {medida.alto} mm
          {orientacion === 'apaisada' && ` · ${area.ancho} × ${area.alto}`}
        </span>

        <span style={{ flex: 1 }} />

        <Herramienta titulo="Deshacer" icono="undo" deshabilitado={paso === 0} onClic={() => setPaso((p) => Math.max(p - 1, 0))} />
        <Herramienta
          titulo="Rehacer"
          icono="redo"
          deshabilitado={paso >= historial.length - 1}
          onClic={() => setPaso((p) => Math.min(p + 1, historial.length - 1))}
        />
        <Herramienta titulo="Alejar" icono="zoom_out" onClic={() => setZoom((z) => Math.max(z / 1.25, 0.8))} />
        <Herramienta titulo="Acercar" icono="zoom_in" onClic={() => setZoom((z) => Math.min(z * 1.25, 10))} />

        <button type="button" onClick={imprimirPrueba} style={{ ...botonSecundario, minHeight: 38 }}>
          <Icono nombre="print" size={17} color={c.texto3} />
          Imprimir prueba
        </button>

        <button
          type="button"
          onClick={guardar}
          disabled={guardando}
          style={{ ...botonPrimario, minHeight: 38, opacity: guardando ? 0.7 : 1 }}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={guardando ? 'g' : 'n'}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.14 }}
              style={{ display: 'inline-block' }}
            >
              {guardando ? 'Guardando…' : 'Guardar'}
            </motion.span>
          </AnimatePresence>
        </button>
      </div>

      {/* ------------------------------------------------- barra de alineación */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ ...rotulo, marginRight: 2 }}>
          {elegidos.length === 0 ? (
            'Nada elegido'
          ) : varios ? (
            <>
              <Numero valor={elegidos.length} /> elegidos
            </>
          ) : (
            NOMBRES[elegidos[0]!.tipo]
          )}
        </span>

        {(
          [
            ['izquierda', 'align_horizontal_left', 'Alinear a la izquierda'],
            ['centroH', 'align_horizontal_center', 'Centrar a lo ancho'],
            ['derecha', 'align_horizontal_right', 'Alinear a la derecha'],
            ['arriba', 'align_vertical_top', 'Alinear arriba'],
            ['centroV', 'align_vertical_center', 'Centrar a lo alto'],
            ['abajo', 'align_vertical_bottom', 'Alinear abajo'],
          ] as [Alineacion, string, string][]
        ).map(([como, icono, titulo]) => (
          <Herramienta
            key={como}
            titulo={varios ? `${titulo} (entre los elegidos)` : `${titulo} (en la etiqueta)`}
            icono={icono}
            deshabilitado={elegidos.length === 0}
            onClic={() => alinearElegidos(como)}
          />
        ))}

        <span style={{ width: 1, height: 24, background: c.borde, margin: '0 4px' }} />

        <Herramienta
          titulo="Repartir a lo ancho"
          icono="horizontal_distribute"
          deshabilitado={elegidos.length < 3}
          onClic={() => repartir('horizontal')}
        />
        <Herramienta
          titulo="Repartir a lo alto"
          icono="vertical_distribute"
          deshabilitado={elegidos.length < 3}
          onClic={() => repartir('vertical')}
        />

        <span style={{ width: 1, height: 24, background: c.borde, margin: '0 4px' }} />

        <Herramienta titulo="Agrupar (Ctrl+G)" icono="join_inner" deshabilitado={!varios} onClic={agrupar} />
        <Herramienta
          titulo="Desagrupar (Ctrl+Shift+G)"
          icono="join_right"
          deshabilitado={!hayGrupo}
          onClic={desagrupar}
        />
        <Herramienta titulo="Duplicar (Ctrl+D)" icono="content_copy" deshabilitado={!elegidos.length} onClic={duplicar} />
        <Herramienta
          titulo="Borrar (Supr)"
          icono="delete"
          deshabilitado={!elegidos.length}
          onClic={() => borrar(seleccion)}
        />

        <span style={{ flex: 1 }} />

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: c.texto2 }}>
          Cuadrícula
          <select
            value={cuadricula}
            onChange={(e) => setCuadricula(Number(e.target.value))}
            style={{ ...campo, height: 32, width: 92 }}
          >
            {CUADRICULAS.map((g) => (
              <option key={g} value={g}>
                {g === 0 ? 'Sin' : `${g} mm`}
              </option>
            ))}
          </select>
        </label>

        <Herramienta
          titulo="Guías de alineación"
          icono="straighten"
          activo={conGuias}
          onClic={() => setConGuias((v) => !v)}
        />
      </div>

      <AnimatePresence>
        {avisoTexto && (
          <motion.div key={avisoTexto.texto} {...abrir(8)} style={aviso(avisoTexto.ok ? 'ok' : 'err')}>
            {avisoTexto.texto}
          </motion.div>
        )}
      </AnimatePresence>

      <div
        id="wms-etiquetas-grid"
        style={{ display: 'grid', gridTemplateColumns: '190px minmax(0,1fr) 260px', gap: 16, alignItems: 'start' }}
      >
        {/* ------------------------------------------------------ elementos */}
        <div style={{ ...tarjeta, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span style={rotulo}>Agregar</span>
          {(Object.keys(NOMBRES) as ElementoEtiqueta['tipo'][]).map((tipo) => (
            <button
              key={tipo}
              type="button"
              onClick={() => agregar(tipo)}
              style={{ ...botonSecundario, minHeight: 38, justifyContent: 'flex-start' }}
            >
              <Icono nombre={ICONOS[tipo]} size={17} color={c.texto3} />
              {NOMBRES[tipo]}
            </button>
          ))}

          <span style={{ ...rotulo, marginTop: 6 }}>Capas</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, position: 'relative' }}>
            <AnimatePresence initial={false} mode="popLayout">
            {[...plantilla.elementos].reverse().map((e) => {
              const activo = seleccion.has(e.id);
              return (
                <motion.button
                  key={e.id}
                  layout="position"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0, backgroundColor: activo ? c.verdeSuave : 'rgba(255,255,255,0)' }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.22, layout: resorte }}
                  type="button"
                  onClick={(ev) => {
                    const suma = ev.ctrlKey || ev.metaKey || ev.shiftKey;
                    const base = suma ? new Set(seleccion) : new Set<string>();
                    if (suma && base.has(e.id)) base.delete(e.id);
                    else base.add(e.id);
                    setSeleccion(conSusGrupos(plantilla.elementos, base));
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    padding: '6px 7px',
                    borderRadius: 8,
                    border: 0,
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <Icono nombre={ICONOS[e.tipo]} size={15} color={activo ? c.verde : c.texto3} />
                  <span
                    style={{
                      fontSize: 12,
                      color: activo ? c.verde : c.texto2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                    }}
                  >
                    {e.contenido || NOMBRES[e.tipo]}
                  </span>
                  {e.grupo && <Icono nombre="join_inner" size={13} color={c.texto4} />}
                </motion.button>
              );
            })}
            </AnimatePresence>
            <AnimatePresence initial={false}>
              {plantilla.elementos.length === 0 && (
                <motion.span {...desplegar} style={{ ...desplegar.style, fontSize: 12, color: c.texto3 }}>
                  La etiqueta está vacía.
                </motion.span>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* --------------------------------------------------------- lienzo */}
        <div style={{ ...tarjeta, padding: 16, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: c.texto2 }}>
              <input type="checkbox" checked={conDatos} onChange={(e) => setConDatos(e.target.checked)} />
              Ver con datos de ejemplo
            </label>
            <span style={{ flex: 1 }} />
            {cajaSeleccion && (
              <span style={{ fontFamily: mono, fontSize: 11.5, color: c.texto3 }}>
                {cajaSeleccion.x.toFixed(1)}, {cajaSeleccion.y.toFixed(1)} · {cajaSeleccion.ancho.toFixed(1)} ×{' '}
                {cajaSeleccion.alto.toFixed(1)} mm
              </span>
            )}
          </div>

          <div style={{ overflow: 'auto', background: c.fondo, borderRadius: 10, padding: 20, minHeight: 320 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `${REGLA}px auto`,
                gridTemplateRows: `${REGLA}px auto`,
                gridTemplateAreas: '"esquina arriba" "izquierda lienzo"',
                width: 'fit-content',
                margin: '0 auto',
                boxShadow: '0 2px 12px rgba(26,26,26,.14)',
                background: '#fff',
              }}
            >
              <Reglas area={area} zoom={zoom} seleccion={cajaSeleccion} />

              <svg
                ref={svg}
                viewBox={`0 0 ${area.ancho} ${area.alto}`}
                width={area.ancho * zoom}
                height={area.alto * zoom}
                style={{ display: 'block', gridArea: 'lienzo', touchAction: 'none' }}
                onPointerDown={alBajarFondo}
                onPointerMove={alMover}
                onPointerUp={alSoltar}
                onPointerCancel={alSoltar}
              >
                <rect x={0} y={0} width={area.ancho} height={area.alto} fill="#fff" />

                {lineasCuadricula.map((l, i) => (
                  <line
                    key={i}
                    x1={l.x1}
                    y1={l.y1}
                    x2={l.x2}
                    y2={l.y2}
                    stroke={c.borde}
                    strokeWidth={0.1}
                    vectorEffect="non-scaling-stroke"
                  />
                ))}

                {plantilla.elementos.map((e) => (
                  <ElementoEditable
                    key={e.id}
                    e={e}
                    datos={datos}
                    elegido={seleccion.has(e.id)}
                    onBajar={(ev, modo) => alBajarElemento(ev, e, modo)}
                  />
                ))}

                {/* Las guías que engancharon, mientras se arrastra */}
                {guias.map((g, i) => (
                  <line
                    key={i}
                    x1={g.eje === 'x' ? g.en : 0}
                    y1={g.eje === 'x' ? 0 : g.en}
                    x2={g.eje === 'x' ? g.en : area.ancho}
                    y2={g.eje === 'x' ? area.alto : g.en}
                    stroke={c.ambar}
                    strokeWidth={0.6}
                    strokeDasharray="2 2"
                    vectorEffect="non-scaling-stroke"
                    style={{ pointerEvents: 'none' }}
                  />
                ))}

                {/* El marco de selección */}
                {marco && (
                  <rect
                    x={Math.min(marco.x0, marco.x1)}
                    y={Math.min(marco.y0, marco.y1)}
                    width={Math.abs(marco.x1 - marco.x0)}
                    height={Math.abs(marco.y1 - marco.y0)}
                    fill={c.verde}
                    fillOpacity={0.08}
                    stroke={c.verde}
                    strokeWidth={0.4}
                    strokeDasharray="1.5 1.5"
                    vectorEffect="non-scaling-stroke"
                    style={{ pointerEvents: 'none' }}
                  />
                )}

                {/* Con varios elegidos, el bloque entero */}
                {varios && cajaSeleccion && (
                  <rect
                    x={cajaSeleccion.x}
                    y={cajaSeleccion.y}
                    width={cajaSeleccion.ancho}
                    height={cajaSeleccion.alto}
                    fill="none"
                    stroke={c.verde}
                    strokeWidth={0.4}
                    strokeDasharray="2 1.5"
                    vectorEffect="non-scaling-stroke"
                    style={{ pointerEvents: 'none' }}
                  />
                )}
              </svg>
            </div>
          </div>

          <div style={nota}>
            Arrastrá para mover y usá el cuadradito de la esquina para el tamaño. Con Ctrl o Shift se eligen varios, y
            arrastrando sobre el fondo se los encierra con un marco. Flechas para mover de a poco, Shift+flechas de a
            diez, Ctrl+D duplica, Ctrl+G agrupa y Ctrl+S guarda.
          </div>

          {/* El código elegido, solo, para poder abrirlo grande y probarlo */}
          {uno && (uno.tipo === 'barras' || uno.tipo === 'qr') && (
            <div id={`wms-codigo-${uno.id}`} style={{ display: 'none' }}>
              <Etiqueta
                plantilla={{ version: 1, elementos: [{ ...uno, x: 2, y: 2, giro: 0 }] }}
                ancho={uno.ancho + 4}
                alto={uno.alto + 4}
                datos={catalogo.muestra}
              />
            </div>
          )}

          {/* Lo que se manda a la impresora: el mismo dibujo, sin las guías */}
          <div id="wms-etiqueta-impresion" style={{ display: 'none' }}>
            <Etiqueta
              plantilla={plantilla}
              ancho={medida.ancho}
              alto={medida.alto}
              orientacion={orientacion}
              datos={catalogo.muestra}
            />
          </div>
        </div>

        {/* ---------------------------------------------------- propiedades */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Propiedades
            elemento={uno}
            medida={area}
            barra={barra}
            onProbarCodigo={probarCodigo}
            onCambiar={(campos) => uno && cambiarElemento(uno.id, campos)}
            onGirar={(giro) => {
              if (!uno) return;
              const girado = { ...uno, giro: normalizarGiro(giro) };
              cambiarElemento(uno.id, { giro: girado.giro, ...acomodarDentro(girado, area.ancho, area.alto) });
            }}
            onBorrar={() => uno && borrar([uno.id])}
            onCapa={capa}
          />

          <AnimatePresence initial={false}>
            {uno && (uno.tipo === 'texto' || uno.tipo === 'barras' || uno.tipo === 'qr') && (
              <motion.div key="variables" {...desplegar}>
                <Variables
                  catalogo={catalogo}
                  uso={uso}
                  onInsertar={(clave) => cambiarElemento(uno.id, { contenido: `${uno.contenido ?? ''}{{${clave}}}` })}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <Papel
            medida={medida}
            orientacion={orientacion}
            uso={uso}
            catalogo={catalogo}
            onCambiar={setMedida}
            onOrientacion={setOrientacion}
            onUso={setUso}
            fuera={fuera}
          />
        </div>
      </div>
    </div>
  );
}

/** El elemento dibujado más sus tiradores, que no se imprimen. */
function ElementoEditable({
  e,
  datos,
  elegido,
  onBajar,
}: {
  e: ElementoEtiqueta;
  datos: Record<string, string>;
  elegido: boolean;
  onBajar: (ev: React.PointerEvent, modo: 'mover' | 'medir') => void;
}) {
  // La zona para agarrarlo acompaña al elemento: girado, el contorno y el
  // tirador van donde el elemento está de verdad y no donde estaba antes.
  const giro = normalizarGiro(e.giro);
  const transform = giro ? `rotate(${giro} ${e.x + e.ancho / 2} ${e.y + e.alto / 2})` : undefined;
  const huella = huellaGirada(e);
  // Una línea es finita: se le agranda la zona para poder tocarla.
  const agarreAlto = Math.max(e.alto, 1.5);

  return (
    <g>
      <ElementoDibujado e={e} datos={datos} />

      <g transform={transform}>
        <rect
          x={e.x}
          y={e.y - (agarreAlto - e.alto) / 2}
          width={e.ancho}
          height={agarreAlto}
          fill="transparent"
          stroke={elegido ? c.verde : 'transparent'}
          strokeWidth={0.3}
          vectorEffect="non-scaling-stroke"
          style={{ cursor: 'move' }}
          onPointerDown={(ev) => onBajar(ev, 'mover')}
        />

        {elegido && (
          <rect
            x={e.x + e.ancho - 1}
            y={e.y + e.alto - 1}
            width={2}
            height={2}
            fill={c.verde}
            style={{ cursor: 'nwse-resize' }}
            onPointerDown={(ev) => onBajar(ev, 'medir')}
          />
        )}
      </g>

      {/* Girado, se marca además el lugar que ocupa: es lo que tiene que
          entrar en la etiqueta. */}
      {elegido && giro !== 0 && giro !== 180 && (
        <rect
          x={huella.x}
          y={huella.y}
          width={huella.ancho}
          height={huella.alto}
          fill="none"
          stroke={c.verde}
          strokeOpacity={0.45}
          strokeDasharray="1 1"
          strokeWidth={0.2}
          vectorEffect="non-scaling-stroke"
          style={{ pointerEvents: 'none' }}
        />
      )}
    </g>
  );
}
